import React, { Fragment, useEffect, useState } from 'react';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { useDispatch, useSelector } from 'react-redux';
import { Link, navigate } from 'gatsby';

import Layout from '@components/Layout';
import { Grid, Divider, Col, Row } from '@components/Grid';
import { makePostRequest } from '@helpers/requests';
import { USER_LOGIN } from '@helpers/api';
import { loginUser } from '@actions/user';
import Alert from '@components/Alert';
import Input from '@components/Input';
import Card from '@components/Card';

import styles from './styles.module.scss';
import { withSnackbar } from '@components/Snackbar';
import SEO from '@components/SEO';

const Login = ({ location, openSnackbar }) => {
    const dispatch = useDispatch();
    const user = useSelector(({ user }) => user);

    useEffect(() => {
        if (user) {
            navigate('/account/overview');
        }
    }, []);

    const handleSubmit = async (values, actions) => {
        try {
            const { data } = await makePostRequest(USER_LOGIN, values);

            await dispatch(loginUser(data));
            await navigate(
                user?.permissions?.emailVerified && location?.state?.from
                    ? `${location?.state?.from?.pathname ?? '/account/overview'}?${location?.state
                          ?.from?.search ?? ''}`
                    : '/account/overview'
            );
        } catch (error) {
            error !== 'cancelled' &&
                openSnackbar(error?.errorMessage ?? 'An error occurred when attempting login.');
        }
    };

    // // If our user is an admin and they haven't already selected a website
    // if (user?.permissions?.isAdmin && !website) {
    //     // Redirect to the admin overview page
    //     return <Redirect to="/admin/dashboard" noThrow />;
    // }

    // user already exists, don't show login
    if (user) {
        return null;
    }

    return (
        <Layout>
            <SEO
                title="Login"
                description="Sylvan arms offers a varitery of tools for your gunsmithing tools and maintenance. Feel free to shop around our site. Enjoy Shopping!"
            />
            <div className={styles.login}>
                <Grid>
                    <Row center="xs" middle="xs">
                        <Col xs={12} md={8} lg={6}>
                            <Row start="xs">
                                <Col xs={12}>
                                    <Formik
                                        validationSchema={yupObjectSchema}
                                        initialValues={{ email: '', password: '' }}
                                        onSubmit={handleSubmit}
                                    >
                                        {({
                                            handleSubmit,
                                            values,
                                            errors,
                                            touched,
                                            isSubmitting,
                                            handleChange,
                                        }) => (
                                            <Fragment>
                                                <Card>
                                                    <Card.Title>Login</Card.Title>
                                                    <Card.Content>
                                                        <Row>
                                                            <Col xs={12}>
                                                                <Input
                                                                    label="Email Address"
                                                                    name="email"
                                                                    type="email"
                                                                    value={values.email}
                                                                    onChange={handleChange}
                                                                />
                                                                {touched.email && errors.email && (
                                                                    <Fragment>
                                                                        <Divider margin={2} />
                                                                        <Alert
                                                                            type="error"
                                                                            message={errors.email}
                                                                        />
                                                                    </Fragment>
                                                                )}
                                                            </Col>
                                                        </Row>

                                                        <Divider margin={2} />

                                                        <Row>
                                                            <Col xs={12}>
                                                                <Input
                                                                    label="Password"
                                                                    name="password"
                                                                    type="password"
                                                                    value={values.password}
                                                                    onChange={handleChange}
                                                                />
                                                                {touched.password &&
                                                                    errors.password && (
                                                                        <Fragment>
                                                                            <Divider margin={2} />
                                                                            <Alert
                                                                                type="error"
                                                                                message={
                                                                                    errors.password
                                                                                }
                                                                            />
                                                                        </Fragment>
                                                                    )}
                                                            </Col>
                                                        </Row>
                                                    </Card.Content>
                                                    <Card.Actions>
                                                        <Card.Actions.Action
                                                            onClick={handleSubmit}
                                                            submitting={isSubmitting}
                                                            text="Login"
                                                        />
                                                        <Card.Actions.Action
                                                            link="/register"
                                                            secondary
                                                            text="Sign up"
                                                        />
                                                    </Card.Actions>
                                                </Card>

                                                <Divider />
                                                <Row>
                                                    <Col xsGrow>
                                                        <p>
                                                            Don't have an account?{' '}
                                                            <Link
                                                                to="/register"
                                                                state={{ from: location }}
                                                            >
                                                                Sign up
                                                            </Link>
                                                        </p>
                                                    </Col>
                                                    <Col xsShrink>
                                                        <p>
                                                            <Link to="/forgot-password">
                                                                I forgot my password
                                                            </Link>
                                                        </p>
                                                    </Col>
                                                </Row>
                                            </Fragment>
                                        )}
                                    </Formik>
                                </Col>
                            </Row>
                        </Col>
                    </Row>
                </Grid>
            </div>
        </Layout>
    );
};

const yupObjectSchema = Yup.object().shape({
    email: Yup.string()
        .email('Must be an email address')
        .lowercase()
        .required('Email Address is required'),
    password: Yup.string().required('Password is required'),
});

export default withSnackbar(Login);
